/**
 * "Play it when it arrives."
 *
 * Pressing play on a track nobody has downloaded yet cannot start playback — there
 * is no file. The app queues a download and tells the user it will be ready in a
 * moment; this is what makes that promise true instead of leaving them to press
 * play a second time.
 *
 * It lives outside any page because the intent has to outlive the component that
 * created it: users start a download from search and then navigate to their
 * library while it runs.
 */

import { create } from 'zustand'

import { api } from '@/api/client'
import type { JobEvent, LibraryItem } from '@/api/types'

import { toQueueItem, usePlayer, type PlaySource } from './store'

export type PlaybackIntent = 'play' | 'next' | 'queue'

/** What was asked for, and where it was asked from — the download can outlive
 *  the page that started it, so the origin has to travel with the intent. */
interface PendingRequest {
  intent: PlaybackIntent
  source: PlaySource
}

interface PendingPlaybackState {
  /** Library item id → what to do with it once the download finishes. */
  pending: Record<number, PendingRequest>
  requestWhenReady: (
    libraryItemId: number,
    intent: PlaybackIntent,
    source?: PlaySource,
  ) => void
  cancel: (libraryItemId: number) => void
  isPending: (libraryItemId: number) => boolean
  /** Fed from the job event stream. */
  handleJobEvent: (event: JobEvent) => void
}

export const usePendingPlayback = create<PendingPlaybackState>((set, get) => ({
  pending: {},

  requestWhenReady: (libraryItemId, intent, source = 'unknown') =>
    set((state) => ({ pending: { ...state.pending, [libraryItemId]: { intent, source } } })),

  cancel: (libraryItemId) =>
    set((state) => {
      if (!(libraryItemId in state.pending)) return state
      const { [libraryItemId]: _removed, ...rest } = state.pending
      return { pending: rest }
    }),

  isPending: (libraryItemId) => libraryItemId in get().pending,

  handleJobEvent: (event) => {
    const id = event.library_item_id
    if (id === null) return

    const request = get().pending[id]
    if (!request) return

    if (event.status === 'failed' || event.status === 'cancelled') {
      get().cancel(id)
      return
    }
    if (event.status !== 'succeeded') return

    // Claim the intent before awaiting, so a duplicate event cannot double-play.
    get().cancel(id)

    void api
      .get<LibraryItem>(`/library/tracks/${id}`)
      .then((item) => {
        const queueItem = toQueueItem(item)
        if (!queueItem) return

        const player = usePlayer.getState()
        switch (request.intent) {
          case 'play':
            player.playQueue([queueItem], 0, { source: request.source })
            break
          case 'next':
            player.playNextUp([queueItem])
            break
          case 'queue':
            player.enqueue([queueItem])
            break
        }
      })
      .catch(() => {
        // The file finished but we could not read it back. The track is in the
        // library either way, so silence here is better than an error toast for
        // something the user can simply press play on.
      })
  },
}))
